import { visibleCardsFor } from './dashboardVisibleCards';

/*
  ════════════════════════════════════════════════════════════════════════════
  cardConfig.js — the ONLY file you edit to add / tweak a dashboard widget.
  ════════════════════════════════════════════════════════════════════════════
  <DynamicCards moduleName="stickyNotes" /> looks the module up here, calls the
  common Auth endpoint (/dashboard/cards?moduleName=...), and renders the LIVE
  response with this config. Pass ONLY the moduleName from the page.
  (The page may also pass `title` to override the heading below.)

  Each entry:
    title    -> heading above the section (page `title` prop overrides it)
    variant  -> 'sticky'     : sticky-note cards (needs the *Key field map below)
                'counts'     : one stat card per number in the response object
                'submission' : "Total Client Submission" look — a header total
                               + one progress bar per number (totalKey/infoText)
                'list'       : stat cards from an array (uses titleKey/countKey/…)
    request  -> how to call the gateway:
                  method 'GET' | 'POST'
                  params { limit, offset, type, … }  -> query string
                  body   { … }                       -> JSON body (POST only)
    listPath -> dot-path to the data inside the API response:
                  'data.data' -> array (sticky/list) ; 'data' -> counts object

  Field map (sticky/list): every key OPTIONAL, [a, b] joins two fields.
  'counts' needs no field map — it auto-labels each number in the response.

  ──────────────────────────────────────────────────────────────────────────
  CUSTOMISE EACH CARD (all optional, work on every variant)
  ──────────────────────────────────────────────────────────────────────────
  Every card has a NAME:
    • counts / submission -> the humanized label   ('inProgress' -> 'In Progress')
    • sticky              -> the value of `colorBy` (e.g. the `status` text)
    • list                -> the first titleKey value

  color    -> base color for ALL cards in the section:
                omit          -> plain (counts) / auto-cycle (sticky)
                'auto'        -> a different palette color per card
                'green'       -> that one fixed color for every card

  colorMap -> per-card color BY NAME (overrides `color`), case-insensitive:
                colorMap: { 'In Progress': 'purple', Hired: 'green', Rejected: 'red' }
              Submission bars accept a palette name OR a raw hex ('#0ea5e9').
              counts / submission: the NAME may be the RAW API key
              ('pendingSubmissionCount') OR the humanized label ('Pending Submission Count').

  labelMap -> (counts / submission) rename a card's displayed text. Key by the
              raw API name OR the humanized label:
                labelMap: { pendingSubmissionCount: 'Pending', submittedToClientCount: 'Submitted' }

  colorBy  -> (sticky) which API field the colorMap/hide names come from:
                colorBy: 'status'  +  colorMap: { Pending: 'peach', Done: 'green' }

  hide     -> drop specific cards. Each entry is EITHER:
                a name   (string)  -> hides the card with that name (case-insensitive;
                                      counts/submission match the raw key OR the label)
                an index (number)  -> hides the card at that 0-based position
                hide: ['Rejected', 0]

  ──────────────────────────────────────────────────────────────────────────
  ALIGNMENT & COUNT
  ──────────────────────────────────────────────────────────────────────────
  layout   -> 'COLSxROWS' — sets columns AND caps the count to cols*rows:
                '4x2' -> 4 across, max 8 cards   '3x3' -> 3 across, max 9
                '2x4' -> 2 across, max 8 cards
  columns  -> just the column count, no cap (e.g. columns: 3).
  maxCount -> just a hard cap on the number of cards, keeps current columns.
              (explicit columns/maxCount win over whatever `layout` implies.)
              Defaults: counts/list = 4 cols, sticky = 2 cols, submission = 1.

  Palette color names (defined in PALETTE / dynamicCard.css):
                blue green purple peach red gray
  (sticky + counts use these as CSS classes; submission uses them as bar colors.)

  EVERYTHING above is also overridable per-placement from the page:
    <DynamicCards moduleName="jobsCount" title="Jobs" layout="4x2" />
*/
export const MODULE_CONFIG = {
  stickyNotes: {
    title: "Sticky Notes",
    variant: "sticky",
    request: { method: "GET", params: { limit: 20, offset: 0 } },
    listPath: "data.data",
    // Where the sticky-note "View all" link navigates to (omit to hide it).
    viewAllUrl: "/viewall-sticky",
    // Dispatched (window.dispatchEvent(new Event(...))) by the add-note popup
    // on a successful save; ModuleCards listens for it and refetches, since
    // closing the popup returns to this same mounted component (no remount).
    refreshEvent: "sticky-notes:refresh",
    pillKey: "status",
    headingKey: "title",
    bodyKey: "notes",
    // Notes sit in a fixed-height card; the body is clamped to this many lines
    // with an ellipsis. Hovering a CLIPPED note shows the full text in a popover
    // (notes that fit show no popover). 2–6 supported (see dynamicCard.css).
    bodyLines: 3,
    footerLeftKey: "createdByName",
    footerRightKey: "createdAt",
    dateKey: "reminder.time",
    // Colour each note from its `status` value (falls back to the auto cycle
    // for any status not listed here). Edit the names to match your API.
    colorBy: "status",
    colorMap: {
      Note: "green",
      Tagged: "red",
    },
    color: "auto",
    // hide: ['Archived'],   // e.g. hide notes whose status is 'Archived'
  },

  jobsCount: {
    variant: "counts",
    dynamicCommonCount: {
      module: "jobs",
      keys: ["jobRecruitmentStatus", "jobStatus"],
    },
    listPath: "",
    layout: "4x2",
    groupSeparator: "",
    labelMap: {
      total: "Total Jobs",
      active: "Active Jobs",
      myAssignedCount: "My Jobs",
    },
    color: "#f1f3f8",
    show: visibleCardsFor("jobsCount"),
    hide: ["inactive", "submitted", "noSubmissions"],
  },

  // Rendered as the "Total Client Submission" card: one header total + a
  // horizontal progress bar per number in the response (ClientSubmissionCard look).
  submissionCount: {
    title: "Total Client Submission",
    tooltipText:
      "Includes all submissions that are pending and submitted to the client.",
    variant: "submission",
    dynamicCommonCount: {
      module: "submissions",
      keys: ["clientSubmissionStatus.clientSubmissionStatusText"],
    },
    listPath: "",
    // 'total' key is preserved in the flat object from the dashboard conversion.
    totalKey: "total",
    labelMap: {
      pendingSubmissionCount: "Pending Submissions",
      rejectedSubmissionCount: "Rejected",
      submittedToClientCount: "Submitted to Client",
    },
    colorMap: {
      pendingSubmissionCount: "rgb(234, 175, 12)",
      submittedToClientCount: "green",
      rejectedSubmissionCount: "red",
    },
    aggregateMap: {
      pendingSubmissionCount: [
        "pendingSubmissionCount",
        "pending",
        "Pending",
        "Under Evaluation",
        "underEvaluation",
      ],
      submittedToClientCount: [
        "submittedToClientCount",
        "Submitted to Client",
        "Submitted To Client",
      ],
      rejectedSubmissionCount: [
        "rejectedSubmissionCount",
        "Rejected",
        "rejected",
      ],
    },
    show: visibleCardsFor("submissionCount"),
  },

  leadAptitude: {
    title: "Aptitude Test Results",
    variant: "assessment",
    request: {
      method: "GET",
      params: {},
    },
    listPath: "data",
    totalKey: "totalScore",
    maxKey: "maximumScore",
    statusKey: "status",
    attemptDateKey: "attemptDate",
    timeTakenKey: "timeTaken",
    rankKey: "rank",
    labelMap: {
      logicalReasoning: "Logical Reasoning",
      quantitativeAptitude: "Quantitative Aptitude",
      verbalAbility: "Verbal Ability",
    },
    colorMap: {
      logicalReasoning: "#7c3aed",
      quantitativeAptitude: "#0891b2",
      verbalAbility: "#16a34a",
    },
    hide: ["status", "attemptDate", "timeTaken", "rank", "maximumScore"],
  },

  onboardingCount: {
    variant: "counts",
    dynamicCommonCount: {
      module: "onboarding",
      keys: ["onboardingObStage"],
    },
    listPath: "",
    frame: true,
    frameTitle: "Onboarding",
    frameClassName: "onboarding-card",
    viewAll: true,
    footer: true,
    columns: 4,
    valueMap: {
      inProgress: "Under Review",
      hired: "Onboarded",
      projectCompleted: "Project cycle closed",
      archive: "No longer active",
    },
    labelMap: {
      inProgress: "In Progress",
      hired: "Hired",
      projectCompleted: "Project Completed",
      archive: "Archive",
    },
    aggregateMap: {
      inProgress: ["inProgress", "In Progress"],
      hired: ["hired", "Hired", "Active"],
      projectCompleted: ["projectCompleted", "Project Completed", "Completed"],
      archive: ["archive", "Archive", "Not Hired", "Candidate Backout"],
    },
    show: visibleCardsFor("onboardingCount"),
    hide: ["total", "totalGrossMargin", "totalNetMargin"],
  },
};

// ── Colour palettes ─────────────────────────────────────────────────────────
// Names here must exist as classes in dynamicCard.css (.dyn-card-item.<name>
// and .sticky-note.<name>). Add a name here AND a class there to extend them.

// counts / list cards cycle through these when color:'auto'.
export const PALETTE = ["blue", "green", "purple", "peach", "red", "gray"];

// sticky notes cycle through these when color:'auto'.
export const STICKY_PALETTE = ["purple", "peach", "green"];

// submission bars cycle through these hex values when no colorMap matches.
export const BAR_PALETTE = [
  "#1d4ed8",
  "#ef4444",
  "#16a34a",
  "#9333ea",
  "#f59e0b",
  "#0891b2",
];

// Palette NAME -> hex, so colorMap palette names work for the inline submission bars.
export const NAMED_COLORS = {
  blue: "#1d4ed8",
  green: "#16a34a",
  purple: "#9333ea",
  peach: "#f97316",
  red: "#ef4444",
  gray: "#6b7280",
};

// Returns the effective config: built-in defaults deep-merged with whatever
// the host project injected via configureCoreTheme({ cardConfig: { … } }) or
// window.__CORE_THEME_CONFIG__.cardConfig. Called at render time so it always
// picks up the host's config even when set after import evaluation.
export function getModuleConfig() {
  const hostConfig =
    (typeof window !== 'undefined' && window.__CORE_THEME_CONFIG__?.cardConfig) ?? {};
  // Shallow merge per key: host entries WIN over built-ins, but built-ins remain
  // unless the host replaces them. To extend a built-in entry use spread:
  //   cardConfig: { jobsCount: { ...builtIn.jobsCount, title: 'Open Positions' } }
  return { ...MODULE_CONFIG, ...hostConfig };
}

export default MODULE_CONFIG;
